import { ReadingApiDto } from "@/model/Patient"
// TODO: uncomment when EMR push and SMS Sent columns are re-added
// import cn from "@/utils/cn"
import { DateTimeFormatter, Instant, ZoneId } from "@js-joda/core"
import { Locale } from "@js-joda/locale"

type ReadingsTableProps = {
  readings: ReadingApiDto[]
  prescriptionType: "diabetes" | "hypertension"
  patientTimeZone: string
}

type ReadingsRowProps = {
  reading: ReadingApiDto
  prescriptionType: "diabetes" | "hypertension"
  isLast: boolean
  patientTimeZone: string
}

const formatTimestamp = (patientTimeZone: string, timestamp: Instant) =>
  timestamp
    .atZone(ZoneId.of(patientTimeZone))
    .format(
      DateTimeFormatter.ofPattern("MMM dd yyyy, h:mm a").withLocale(
        Locale.US
      )
    )

// TODO: uncomment when EMR push and SMS Sent columns are re-added
// const getStatusBadgeClass = (
//   status?: "complete" | "pending" | "failed"
// ): string => {
//   switch (status) {
//     case "complete":
//       return "bg-green-100 text-green-700"
//     case "pending":
//       return "bg-yellow-100 text-yellow-700"
//     case "failed":
//       return "bg-red-100 text-red-700"
//     default:
//       return "bg-gray-100 text-gray-700"
//   }
// }

const ReadingRow = ({
  reading,
  prescriptionType,
  isLast,
  patientTimeZone,
}: ReadingsRowProps) => {
  const timestamp = Instant.parse(reading.dateTime)
  const readingId = `${reading.deviceId}-${reading.dateTime}`

  // Get the reading value based on prescription type
  const readingValue =
    prescriptionType === "diabetes"
      ? (reading.bloodGlucoseInMilligramsPerDeciliter ?? "N/A")
      : (reading.bloodPressure ?? "N/A")

  return (
    <tr
      key={readingId}
      className={
        isLast ? "" : "border-b border-gray-200"
      }
    >
      <td className="px-4 py-3 text-sm">{formatTimestamp(patientTimeZone, timestamp)}</td>
      {/* TODO show the reading value fetched from SmartMeter
      <td className="px-4 py-3 text-left text-sm">{readingValue}</td>
      */}
      <td className="px-4 py-3 text-left text-sm">{readingValue}</td>
      {prescriptionType === "diabetes" && (
        <td className="px-4 py-3 text-sm">
          {reading.formContext?.type || "-"}
        </td>
      )}
      <td className="px-4 py-3 text-sm capitalize">
        {reading.classification}
      </td>
      <td className="px-4 py-3 text-sm">{reading.deviceId}</td>
      {/* TODO: Add back EMR push and SMS Sent columns
      <td className="px-4 py-3">
        <span
          className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${getStatusBadgeClass(reading.emrPushStatus)}`}
        >
          {reading.emrPushStatus || "N/A"}
        </span>
      </td>
      <td
        className={cn(
          "px-4 py-3 text-sm",
          isLast && "rounded-br-xl"
        )}
      >
        {reading.smsSent === undefined || reading.smsSent === null ? (
          <span className="text-gray-500">N/A</span>
        ) : reading.smsSent ? (
          <span className="material-icons text-base text-green-700">
            check
          </span>
        ) : (
          <span className="material-icons text-base text-gray-500">
            close
          </span>
        )}
      </td>
      */}
    </tr>
  )
}

// TODO: Should we add pagination to this component?
// TODO: Should we add a way to sort the readings by columns?
const ReadingsTable = ({
  readings,
  prescriptionType,
  patientTimeZone,
}: ReadingsTableProps) => {
  if (readings.length === 0) {
    return (
      <div className="py-8 text-center italic text-gray-500">
        No readings available
      </div>
    )
  }



  return (
    <div className="max-h-[800px] overflow-x-auto overflow-y-auto">
      <table className="w-full">
        <thead className="sticky top-0 bg-gray-100">
          <tr>
            <th className="rounded-tl-xl px-4 py-3 text-left text-sm font-semibold">
              Timestamp
            </th>
            {/* TODO: Add back 3rd party cloud column
            <th className="px-4 py-3 text-left text-sm font-semibold">
              3rd party cloud
              <br />
              (e.g. Smartmeter)
            </th>
            */}
            <th className="px-4 py-3 text-left text-sm font-semibold">
              Goodall SW
            </th>
            {prescriptionType === "diabetes" && (
              <th className="px-4 py-3 text-left text-sm font-semibold">
                Contextualization
              </th>
            )}
            <th className="px-4 py-3 text-left text-sm font-semibold">
              Classification
            </th>
            <th className="px-4 py-3 text-left text-sm font-semibold">
              Device ID
            </th>
            {/* TODO: Add back EMR push and SMS Sent columns
            <th className="px-4 py-3 text-left text-sm font-semibold">
              EMR push
            </th>
            <th className="rounded-tr-xl px-4 py-3 text-left text-sm font-semibold">
              SMS Sent
            </th>
            */}
          </tr>
        </thead>
        <tbody>
          {readings.map((reading, index) => (
            <ReadingRow
              key={`${reading.deviceId}-${reading.dateTime}`}
              reading={reading}
              isLast={index === readings.length - 1}
              patientTimeZone={patientTimeZone}
              prescriptionType={prescriptionType}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default ReadingsTable
